import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"


import { Centered } from "@/components/Centered/Centered.ts"
import { DiffFileComponent } from "@/components/DiffFile/DiffFile.tsx"
import { Empty } from "@/components/Empty/Empty.tsx"
import { Vertical } from "@/components/Flex.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { ChangedFilesEmpty, SvgIllustration } from "@/illustrations/Illustrations.ts"
import { formatErrorMessage } from "@/lib/displayUtils.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { GitCommitChange, InlineDiff } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Property } from "csstype"
import type { IObservableValue } from "mobx"


export const DiffView = observer((props: {
  model: DiffViewModel
}) => {
  const vm = props.model
  const files = vm.files.get()

  if (vm.isLoading.get() || (files.length > 0 && files.map(it => it.diff).every(it => it === null))) {
    return <Centered><Spinner text={ellipsize("Loading diff")} /></Centered>
  }

  if (vm.error.get()) {
    return <div>{formatErrorMessage(vm.error.get(), "Error")}</div>
  }

  if (files.length === 0) {
    return (
      <Empty
        illustration={<ChangedFilesEmpty className={SvgIllustration} />}
        text="Changed files will appear here once the agent creates the first version"
      />
    )
  }

  return (
    <>
      <Container>
        {files.map(item =>
          <DiffFileComponent
            key={keyOf(item.file)}
            file={item.file}
            diff={item.diff}
            gutterWidth={vm.guttersWidth}
          />
        )}
      </Container>
    </>
  )
})

export type DiffFile = {
  file: GitCommitChange
  diff: InlineDiff | null
}

export interface DiffViewModel {
  files: IObservableValue<DiffFile[]>
  error: IObservableValue<Error | null>
  isLoading: IObservableValue<boolean>

  guttersWidth: IObservableValue<Property.Width>

  loadChanges(loadDiff: boolean): Promise<void>

  downloadPatch(): void
}

const Container = styled(Vertical)`
  gap: 12px;
`

function keyOf(item: GitCommitChange) {
  return item.new?.path || item.old?.path || ""
}